import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
    Certificate,
    CertificateRequestsService,
    CertificatesService,
    CertRequest,
    IssuanceStatus,
    RevocationStatusEnum,
} from "@hydrantid/acm-client";
import { MessageService } from "../../services/message/message.service";
import { Router } from "@angular/router";
import { environment } from "../../../configuration";

export interface CertificateStatusRequest {
    certificate?: Certificate;
    certRequest?: CertRequest;
}
@Component({
    selector: "app-dialog-certificate-status",
    templateUrl: "./dialog-certificate-status.component.html",
    styleUrls: ["./dialog-certificate-status.component.scss"],
    standalone: false,
})
export class DialogCertificateStatusComponent implements OnInit {
    currentStatus = this.data.certificate ? "Revoking certificate..." : "Processing certificate...";
    statusCode = "PENDING";
    errorCount = 0;
    timer: number | undefined;
    loadTime = environment.timeoutStatusMilliseconds;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: CertificateStatusRequest,
        private dialogRef: MatDialogRef<DialogCertificateStatusComponent>,
        private certificateService: CertificatesService,
        private csrService: CertificateRequestsService,
        private messageService: MessageService,
        private router: Router,
    ) {}

    hide(): void {
        this.dialogRef.close(this.statusCode);
    }

    handleStatus(
        status: IssuanceStatus | RevocationStatusEnum,
        certificateId: string | undefined,
        message: string | undefined,
    ): void {
        switch (status) {
            case IssuanceStatus.ISSUED:
                this.messageService.success("The certificate request has been processed");
                this.router.navigate(["/certificates", certificateId]);
                this.hide();
                break;
            case RevocationStatusEnum.REVOKED:
                this.messageService.success("The certificate has been revoked");
                this.router.navigate(["/certificates", certificateId]);
                this.hide();
                break;
            case IssuanceStatus.PENDING:
            case IssuanceStatus.IN_PROCESS:
            case RevocationStatusEnum.PENDING:
            case RevocationStatusEnum.IN_PROCESS:
                this.errorCount = 0;
                this.nextLoad();
                break;
            case IssuanceStatus.APPROVAL_REQUIRED:
                this.messageService.info("The request has been submitted and is awaiting approval");
                this.router.navigate(["/requests"]);
                this.hide();
                break;
            case IssuanceStatus.FAILED:
                this.statusCode = "FAILED";
                this.currentStatus = message ?? "Unknown Error";
                break;
        }
    }

    getStatus(): void {
        if (this.data.certRequest) {
            this.csrService.csrIdStatusGet(this.data.certRequest.id).subscribe(
                (status) =>
                    this.handleStatus(
                        status.issuanceStatus,
                        status.certificateId,
                        //@ts-ignore
                        status.issuanceStatusDetails?.Message,
                    ),
                () => {
                    this.currentStatus = "FAILED";
                    this.nextLoad(++this.errorCount * 2 * this.loadTime);
                },
            );
        } else if (this.data.certificate) {
            this.certificateService.certificatesIdStatusGet(this.data.certificate.id).subscribe(
                // Even though the certificate is passed into the function, it might be undefined here? IDK.
                /* istanbul ignore next */
                (status) => this.handleStatus(status.revocationStatus, this.data.certificate?.id, undefined),
                () => {
                    this.currentStatus = "FAILED";
                    this.nextLoad(++this.errorCount * 2 * this.loadTime);
                },
            );
        } else {
            this.dialogRef.close();
        }
    }

    cancelNextLoad(): void {
        if (this.timer) {
            clearTimeout(this.timer);
        }
    }

    nextLoad(milliseconds?: number | undefined): void {
        this.cancelNextLoad();
        this.timer = window.setTimeout(() => {
            this.getStatus();
        }, milliseconds || this.loadTime);
    }

    ngOnInit(): void {
        this.getStatus();
    }

    ngOnDestroy(): void {
        this.cancelNextLoad();
    }
}
